import React, { useState, useEffect } from 'react';
import { FaTimes, FaCheckCircle, FaTimesCircle, FaClock, FaChartLine, FaCalendarAlt, FaArrowLeft, FaArrowRight, FaFilter } from 'react-icons/fa';
import HttpInterceptor from '../lib/HttpInterceptor';
import { formatDateLocale, formatDate } from '../utils/dateFormatter';

/**
 * Attendance Calendar Modal Component
 * Shows attendance calendar view with history and analytics
 */
function AttendanceCalendarModal({ isOpen, onClose, studentId, studentName, studentStatus, isConsultant }) {
  const [attendanceData, setAttendanceData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedStatus, setSelectedStatus] = useState('all'); // all, attended, missed, excused
  const [viewMode, setViewMode] = useState('calendar'); // calendar, list, analytics
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    if (isOpen && studentId) {
      fetchAttendanceData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, studentId]);

  const fetchAttendanceData = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      
      // Check if student is pending approval
      if (studentStatus === 'pending') {
        setErrorMessage('This student is pending approval. Attendance data will be available once the student is approved.');
        setIsLoading(false);
        return;
      }
      
      const response = await HttpInterceptor.get(`/api/students/${studentId}/progress`);
      setAttendanceData(response.data);
    } catch (error) {
      console.error('Error fetching attendance data:', error);
      
      // Extract specific error message from API response
      const errorResponse = error?.response?.data;
      let errorMsg = 'Failed to load attendance data';
      
      if (errorResponse?.message) {
        const apiMessage = errorResponse.message.toLowerCase();
        
        if (apiMessage.includes('not found') || apiMessage.includes('student not found')) {
          errorMsg = isConsultant
            ? 'Client not found. Please ensure the client has booked an appointment.'
            : 'Student not found. Please ensure the student is properly enrolled.';
        } else if (apiMessage.includes('not enrolled') || apiMessage.includes('enrolled in any program')) {
          errorMsg = isConsultant
            ? 'Client has not booked any appointments yet.'
            : 'Student is not enrolled in any program yet.';
        } else if (apiMessage.includes('unauthorized') || apiMessage.includes('not authorized')) {
          errorMsg = 'You are not authorized to view this student\'s attendance data.';
        } else if (apiMessage.includes('pending') || apiMessage.includes('approval')) {
          errorMsg = 'This student is pending approval. Attendance data will be available once the student is approved.';
        } else {
          errorMsg = errorResponse.message;
        }
      } else if (error?.message) {
        errorMsg = error.message;
      }
      
      setErrorMessage(errorMsg);
      // Don't show toast - error is displayed in the modal UI
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;


  const getStatusColor = (status) => {
    switch (status) {
      case 'attended':
        return 'bg-green-500 text-white';
      case 'missed':
        return 'bg-red-500 text-white';
      case 'excused':
        return 'bg-yellow-500 text-white';
      case 'scheduled':
        return 'bg-blue-500 text-white';
      case 'pending':
        return 'bg-amber-400 text-white';
      case 'expired':
        return 'bg-gray-400 text-white';
      default:
        return 'bg-gray-300 text-gray-600';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'attended':
        return <FaCheckCircle className="text-green-600" />;
      case 'missed':
        return <FaTimesCircle className="text-red-600" />;
      case 'excused':
        return <FaClock className="text-yellow-600" />;
      case 'scheduled':
        return <FaCalendarAlt className="text-blue-600" />;
      case 'pending':
        return <FaClock className="text-amber-500" />;
      case 'expired':
        return <FaClock className="text-gray-500" />;
      default:
        return null;
    }
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      days.push(date);
    }

    return days;
  };

  const getAttendanceForDate = (date) => {
    if (!attendanceData?.attendanceByClass) return null;
    
    const dateStr = date.toISOString().split('T')[0];
    for (const classItem of attendanceData.attendanceByClass) {
      if (classItem.attendanceRecords) {
        const record = classItem.attendanceRecords.find(r => {
          const recordDate = new Date(r.sessionDate).toISOString().split('T')[0];
          return recordDate === dateStr;
        });
        if (record) {
          return {
            status: record.status,
            classType: classItem.class?.classType,
            time: classItem.class?.time
          };
        }
      }
    }
    return null;
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const attendanceByClass = attendanceData?.attendanceByClass || [];
  const allRecords = attendanceByClass.flatMap(classItem => 
    (classItem.attendanceRecords || []).map(record => ({
      ...record,
      classType: classItem.class?.classType,
      time: classItem.class?.time,
      day: classItem.class?.day
    }))
  ).sort((a, b) => new Date(b.sessionDate) - new Date(a.sessionDate));

  const filteredRecords = selectedStatus === 'all' 
    ? allRecords 
    : allRecords.filter(r => r.status === selectedStatus);

  // Calculate analytics
  const analytics = {
    total: allRecords.length,
    attended: allRecords.filter(r => r.status === 'attended').length,
    missed: allRecords.filter(r => r.status === 'missed').length,
    excused: allRecords.filter(r => r.status === 'excused').length,
    streak: calculateStreak(allRecords),
    bestDay: calculateBestDay(allRecords),
    worstDay: calculateWorstDay(allRecords)
  };

  function calculateStreak(records) {
    let streak = 0;
    const sorted = [...records].sort((a, b) => new Date(b.sessionDate) - new Date(a.sessionDate));
    for (const record of sorted) {
      if (record.status === 'attended') {
        streak++;
      } else {
        break;
      }
    }
    return streak;
  }

  function calculateBestDay(records) {
    const dayCounts = {};
    records.filter(r => r.status === 'attended').forEach(r => {
      const day = formatDateLocale(r.sessionDate, null, { weekday: 'long' });
      dayCounts[day] = (dayCounts[day] || 0) + 1;
    });
    return Object.entries(dayCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A';
  }

  function calculateWorstDay(records) {
    const dayCounts = {};
    records.filter(r => r.status === 'missed').forEach(r => {
      const day = formatDateLocale(r.sessionDate, null, { weekday: 'long' });
      dayCounts[day] = (dayCounts[day] || 0) + 1;
    });
    return Object.entries(dayCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A';
  }

  const calendarDays = generateCalendarDays();
  const monthName = formatDateLocale(currentMonth.toISOString(), null, { month: 'long', year: 'numeric' });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {isConsultant ? 'Appointment History' : 'Attendance Calendar'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">{studentName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md p-2"
            aria-label="Close modal"
          >
            <FaTimes className="h-5 w-5" />
          </button>
        </div>

        {/* View Mode Tabs */}
        <div className="border-b border-gray-200 px-6">
          <div className="flex space-x-1">
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                viewMode === 'calendar'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <FaCalendarAlt className="inline mr-2" />
              {isConsultant ? 'Schedule' : 'Calendar'}
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                viewMode === 'list'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              History
            </button>
            <button
              onClick={() => setViewMode('analytics')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                viewMode === 'analytics'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <FaChartLine className="inline mr-2" />
              Analytics
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading attendance data...</p>
            </div>
          ) : errorMessage ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <p className="text-gray-700 font-semibold mb-2">{errorMessage}</p>
              {studentStatus === 'pending' && (
                <p className="text-gray-500 text-sm">
                  {isConsultant 
                    ? 'Please approve the appointment to enable tracking.' 
                    : 'Please approve the student to enable attendance tracking.'}
                </p>
              )}
            </div>
          ) : (
            <>
              {viewMode === 'calendar' && (
                <div>
                  {/* Month Navigation */}
                  <div className="flex items-center justify-between mb-6">
                    <button
                      onClick={() => navigateMonth(-1)}
                      className="p-2 hover:bg-gray-100 rounded-lg"
                    >
                      <FaArrowLeft />
                    </button>
                    <h3 className="text-xl font-semibold text-gray-900">{monthName}</h3>
                    <button
                      onClick={() => navigateMonth(1)}
                      className="p-2 hover:bg-gray-100 rounded-lg"
                    >
                      <FaArrowRight />
                    </button>
                  </div>

                  {/* Calendar Grid */}
                  <div className="grid grid-cols-7 gap-2">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                      <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                        {day}
                      </div>
                    ))}
                    {calendarDays.map((date, index) => {
                      if (!date) {
                        return <div key={index} className="aspect-square"></div>;
                      }
                      const attendance = getAttendanceForDate(date);
                      const isToday = date.toDateString() === new Date().toDateString();
                      
                      return (
                        <div
                          key={index}
                          className={`aspect-square border-2 rounded-lg p-2 flex flex-col items-center justify-center ${
                            isToday ? 'border-blue-500' : 'border-gray-200'
                          } ${
                            attendance ? getStatusColor(attendance.status) : 'bg-gray-50'
                          }`}
                        >
                          <span className={`text-sm font-medium ${attendance ? 'text-white' : 'text-gray-600'}`}>
                            {date.getDate()}
                          </span>
                          {attendance && (
                            <span className="text-xs mt-1">{attendance.classType}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Legend */}
                  <div className="mt-6 flex flex-wrap gap-4 justify-center">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-green-500 rounded"></div>
                      <span className="text-sm text-gray-600">{isConsultant ? 'Completed' : 'Attended'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-red-500 rounded"></div>
                      <span className="text-sm text-gray-600">{isConsultant ? 'Cancelled' : 'Missed'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-blue-500 rounded"></div>
                      <span className="text-sm text-gray-600">{isConsultant ? 'Confirmed' : 'Scheduled'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-amber-400 rounded"></div>
                      <span className="text-sm text-gray-600">Pending</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-gray-300 rounded"></div>
                      <span className="text-sm text-gray-600">Not Scheduled</span>
                    </div>
                  </div>
                </div>
              )}

              {viewMode === 'list' && (
                <div>
                  {/* Filter */}
                  <div className="mb-4 flex items-center gap-3">
                    <FaFilter className="text-gray-400" />
                    <select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="all">All Status</option>
                      <option value="attended">{isConsultant ? 'Completed' : 'Attended'}</option>
                      <option value="missed">{isConsultant ? 'Cancelled' : 'Missed'}</option>
                      <option value="excused">{isConsultant ? 'Rescheduled' : 'Excused'}</option>
                    </select>
                    <span className="text-sm text-gray-600">
                      {filteredRecords.length} record{filteredRecords.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {/* Attendance List */}
                  <div className="space-y-3">
                    {filteredRecords.length === 0 ? (
                      <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <p className="text-gray-500">No attendance records found</p>
                      </div>
                    ) : (
                      filteredRecords.map((record, index) => (
                        <div
                          key={index}
                          className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {getStatusIcon(record.status)}
                              <div>
                                <p className="font-semibold text-gray-900">
                                  {formatDate(record.sessionDate)}
                                </p>
                                <p className="text-sm text-gray-600">
                                  {record.classType} - {record.day} {record.time}
                                </p>
                              </div>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              (isConsultant && record.status === 'scheduled' && new Date(record.sessionDate) < new Date()) ? 'bg-gray-100 text-gray-800' :
                              record.status === 'attended' ? 'bg-green-100 text-green-800' :
                              record.status === 'missed' ? 'bg-red-100 text-red-800' :
                              record.status === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                              record.status === 'expired' ? 'bg-gray-100 text-gray-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {isConsultant && record.status === 'scheduled' && new Date(record.sessionDate) < new Date() ? 'expired' :
                               isConsultant && record.status === 'attended' ? 'completed' : 
                               isConsultant && record.status === 'missed' ? 'cancelled' : 
                               record.status}
                            </span>
                          </div>
                          {record.notes && (
                            <p className="mt-2 text-sm text-gray-600 italic">{record.notes}</p>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {viewMode === 'analytics' && (
                <div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-sm text-green-600 font-medium">{isConsultant ? 'Completed' : 'Attended'}</p>
                      <p className="text-2xl font-bold text-green-700 mt-1">{analytics.attended}</p>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-sm text-red-600 font-medium">{isConsultant ? 'Cancelled' : 'Missed'}</p>
                      <p className="text-2xl font-bold text-red-700 mt-1">{analytics.missed}</p>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-sm text-yellow-600 font-medium">{isConsultant ? 'Rescheduled' : 'Excused'}</p>
                      <p className="text-2xl font-bold text-yellow-700 mt-1">{analytics.excused}</p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-600 font-medium">Current Streak</p>
                      <p className="text-2xl font-bold text-blue-700 mt-1">{analytics.streak}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">
                        {isConsultant ? 'Most Active Day' : 'Best Attendance Day'}
                      </h3>
                      <p className="text-2xl text-green-600">{analytics.bestDay}</p>
                    </div>
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">
                        {isConsultant ? 'Most Cancelled Day' : 'Worst Attendance Day'}
                      </h3>
                      <p className="text-2xl text-red-600">{analytics.worstDay}</p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default AttendanceCalendarModal;

